import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import LoadingSection from "../LoadingSection";
import { closeModal, setHasToRefresh } from "../../redux/rightModalSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import { getSuppliersAll, selectSuppliersAll } from "../../redux/suppliers/suppliersSlice";

const MOVEMENT_TYPES = [
    { value: 'Carico', label: 'Carico' },
    { value: 'Scarico', label: 'Scarico' },
    { value: 'Reso', label: 'Reso' },
    { value: 'Trasferimento', label: 'Trasferimento' }
];

export default function AddEditProductMovement() {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const suppliersAll = useSelector(selectSuppliersAll);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        movementType: 'Carico',
        movementDate: new Date().toISOString().split('T')[0],
        productId: '',
        productLabel: '',
        productCode: '',
        productVariantId: '',
        supplierId: '',
        supplierLabel: '',
        supplierCode: '',
        brandLabel: '',
        quantity: 1,
        purchasePrice: 0,
        purchasePriceDiscount: 0,
        purchasePriceDiscounted: 0,
        serialnumber: '',
        barcode: '',
        notes: ''
    });

    const [validation, setValidation] = useState({
        movementType: true,
        quantity: true
    });

    // Carica fornitori
    useEffect(() => {
        if (accountData?._id && (!suppliersAll || suppliersAll.length === 0)) {
            dispatch(getSuppliersAll({ accountId: accountData._id, token }));
        }
    }, [dispatch, accountData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };

            // Auto-calcolo prezzo scontato
            if (name === 'purchasePrice' || name === 'purchasePriceDiscount') {
                const price = name === 'purchasePrice' ? parseFloat(value) || 0 : parseFloat(prev.purchasePrice) || 0;
                const discount = name === 'purchasePriceDiscount' ? parseFloat(value) || 0 : parseFloat(prev.purchasePriceDiscount) || 0;
                updated.purchasePriceDiscounted = parseFloat((price - (price * discount / 100)).toFixed(2));
            }

            // Imposta quantita' negativa per scarico
            if (name === 'movementType') {
                if (value === 'Scarico') {
                    updated.quantity = -Math.abs(updated.quantity);
                } else {
                    updated.quantity = Math.abs(updated.quantity);
                }
            }

            return updated;
        });

        if (name in validation) {
            setValidation(prev => ({ ...prev, [name]: true }));
        }
    };

    const handleSupplierChange = (e) => {
        const supplierId = e.target.value;
        const supplier = suppliersAll?.find(s => (s._id || s.id) === supplierId);
        setFormData(prev => ({
            ...prev,
            supplierId,
            supplierLabel: supplier?.company || ''
        }));
    };

    const validateForm = () => {
        const newValidation = {
            movementType: !!formData.movementType,
            quantity: formData.quantity !== 0 && formData.quantity !== '' && formData.quantity !== undefined
        };
        setValidation(newValidation);
        return Object.values(newValidation).every(v => v);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setSaving(true);
        setError(null);

        try {
            const movementData = {
                ...formData,
                accountId: accountData._id,
                movementDate: formData.movementDate ? new Date(formData.movementDate) : new Date(),
                quantity: formData.movementType === 'Scarico' ? -Math.abs(parseFloat(formData.quantity)) : Math.abs(parseFloat(formData.quantity))
            };

            const url = `${import.meta.env.VITE_API_BASE_URL}/products/addMovement`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({ movement: movementData })
            });

            if (!response.ok) {
                throw new Error('Errore durante il salvataggio del movimento');
            }

            dispatch(closeModal());
            dispatch(setHasToRefresh());
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">Nuovo Movimento Prodotto</h2>

            {saving && <LoadingSection isFixed />}

            <form onSubmit={handleSubmit}>
                {/* Tipo e Data */}
                <h3 className="text-lg font-semibold mb-3">Dati Movimento</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Movimento*</label>
                        <select
                            name="movementType"
                            value={formData.movementType}
                            onChange={handleInputChange}
                            className={`w-full p-2 border ${!validation.movementType ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        >
                            {MOVEMENT_TYPES.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Data Movimento</label>
                        <input
                            type="date"
                            name="movementDate"
                            value={formData.movementDate}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                {/* Prodotto */}
                <h3 className="text-lg font-semibold mb-3 mt-4">Prodotto</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nome Prodotto</label>
                        <input
                            type="text"
                            name="productLabel"
                            value={formData.productLabel}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Nome prodotto"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Codice Prodotto</label>
                        <input
                            type="text"
                            name="productCode"
                            value={formData.productCode}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Codice"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Numero Seriale</label>
                        <input
                            type="text"
                            name="serialnumber"
                            value={formData.serialnumber}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="S/N"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                        <input
                            type="text"
                            name="barcode"
                            value={formData.barcode}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Barcode"
                        />
                    </div>
                </div>

                {/* Fornitore */}
                <h3 className="text-lg font-semibold mb-3 mt-4">Fornitore</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Fornitore</label>
                        <select
                            value={formData.supplierId}
                            onChange={handleSupplierChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">-- Seleziona Fornitore --</option>
                            {suppliersAll?.map(s => (
                                <option key={s._id || s.id} value={s._id || s.id}>{s.company}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fornitore</label>
                        <input
                            type="text"
                            name="supplierCode"
                            value={formData.supplierCode}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Codice fornitore"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                        <input
                            type="text"
                            name="brandLabel"
                            value={formData.brandLabel}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Marca"
                        />
                    </div>
                </div>

                {/* Quantita' e Prezzi */}
                <h3 className="text-lg font-semibold mb-3 mt-4">Quantità e Prezzi</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantità*</label>
                        <input
                            type="number"
                            name="quantity"
                            value={Math.abs(formData.quantity)}
                            onChange={handleInputChange}
                            className={`w-full p-2 border ${!validation.quantity ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            min="0"
                            step="0.01"
                        />
                        {!validation.quantity && (
                            <p className="text-red-500 text-sm mt-1">La quantità è obbligatoria</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Acquisto</label>
                        <input
                            type="number"
                            name="purchasePrice"
                            value={formData.purchasePrice}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min="0"
                            step="0.01"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sconto %</label>
                        <input
                            type="number"
                            name="purchasePriceDiscount"
                            value={formData.purchasePriceDiscount}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min="0"
                            max="100"
                            step="0.01"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Scontato</label>
                        <input
                            type="number"
                            value={formData.purchasePriceDiscounted}
                            readOnly
                            className="w-full p-2 border border-gray-200 rounded-md bg-gray-100"
                        />
                    </div>
                </div>

                {/* Error message */}
                {error && (
                    <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">
                        <p>{error}</p>
                    </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        className="secondary"
                        onClick={() => dispatch(closeModal())}
                        disabled={saving}
                    >
                        Annulla
                    </button>
                    <button
                        type="submit"
                        className="primary"
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                Salvataggio...
                            </>
                        ) : (
                            <>
                                <DynamicIcon iconClass="fas fa-save" />
                                Registra Movimento
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
